import React from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  IconButton,
  Switch,
  Typography,
  useTheme
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import {
  getProviderAum,
  getAdvisorsAcrossProviders,
  setExpandedAdvisor,
  setExpandedProvider,
  setBookAnalysisUI,
  getFullBook
} from '../../../../redux/aum/aum.slice';

import GreatDatePicker from '../../../../components/ui/forms/fields/datePicker';
import PrimaryButton from '../../../../components/ui/buttons/primaryButton';
import { FTable, FTableCell } from '../../../../components/ui/table';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import SilverBoomReport from '../../../../resources/images/logos/SilverBoom Report.svg';
import { fontSize } from '@mui/system';
import PlatformAUMChart from './platformAUMChart';
import BookAUMComposition from './bookAUMComposition';

function ProviderAndAdvisorSummary() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const officeId = useSelector((state) => state.auth.user.tenant_id);

  const { businessDays } = useSelector((state) => state.businessDay);

  const latestBalanceDate = useMemo(() => {
    return businessDays?.date ? dayjs(businessDays.date) : dayjs().subtract(2, 'day');
  }, [businessDays?.date]);

  const {
    providerAum,
    advisorsAcrossProviders,
    expandedProvider,
    expandedAdvisor,
    bookAnalysisUI
  } = useSelector((state) => state.aum);

  const lastMonth = dayjs().subtract(1, 'month');

  const isMonthEnd = bookAnalysisUI?.isMonthEnd ?? true;

  const [providerSort, setProviderSort] = useState({
    field: 'name',
    direction: 'asc'
  });

  const [advisorSort, setAdvisorSort] = useState({
    field: 'name',
    direction: 'asc'
  });

  const { control, setValue } = useForm({
    defaultValues: {
      selectedMonth: bookAnalysisUI?.selectedMonth
        ? dayjs(bookAnalysisUI.selectedMonth)
        : lastMonth.endOf('month')
    }
  });

  useEffect(() => {
    if (!bookAnalysisUI?.selectedMonth) return;

    const date = bookAnalysisUI.isMonthEnd
      ? dayjs(bookAnalysisUI.selectedMonth).endOf('month').format('YYYY-MM-DD')
      : latestBalanceDate.format('YYYY-MM-DD');

    dispatch(
      getFullBook({
        fromDate: date,
        toDate: date
      })
    );
  }, [dispatch, latestBalanceDate, bookAnalysisUI?.selectedMonth, bookAnalysisUI?.isMonthEnd]);

  useEffect(() => {
    if (bookAnalysisUI?.selectedMonth) return;

    dispatch(
      setBookAnalysisUI({
        isMonthEnd: true,
        selectedMonth: lastMonth.endOf('month').format('YYYY-MM-DD')
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (!bookAnalysisUI?.selectedMonth) return;

    const selectedDate = bookAnalysisUI.isMonthEnd
      ? dayjs(bookAnalysisUI.selectedMonth).endOf('month').format('YYYY-MM-DD')
      : latestBalanceDate.format('YYYY-MM-DD');

    dispatch(
      getProviderAum({
        officeId,
        partnerId: '',
        serviceProviderId: '',
        fromDate: selectedDate
      })
    );

    dispatch(
      getAdvisorsAcrossProviders({
        officeId,
        fromDate: selectedDate
      })
    );
  }, [
    dispatch,
    officeId,
    latestBalanceDate,
    bookAnalysisUI?.selectedMonth,
    bookAnalysisUI?.isMonthEnd
  ]);

  const selectedMonth = useWatch({
    control,
    name: 'selectedMonth'
  });

  const sortedProviders = [...(providerAum || [])].sort((a, b) => {
    const { field, direction } = providerSort;

    let result = 0;

    if (field === 'name') {
      result = (a.name || '').localeCompare(b.name || '');
    }

    if (field === 'sumSys') {
      result = (a.sumSys || 0) - (b.sumSys || 0);
    }

    return direction === 'asc' ? result : -result;
  });

  const sortedAdvisors = [...(advisorsAcrossProviders || [])].sort((a, b) => {
    const { field, direction } = advisorSort;

    let result = 0;

    if (field === 'name') {
      result = (a.name || '').localeCompare(b.name || '');
    }

    if (field === 'sumSys') {
      result = (a.sumSys || 0) - (b.sumSys || 0);
    }

    return direction === 'asc' ? result : -result;
  });

  const handleSort = (setter, current, field) => {
    setter({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const SortIcon = ({ active, direction }) => {
    if (!active) {
      return <ArrowUpwardIcon fontSize="small" sx={{ opacity: 0.4 }} />;
    }

    return direction === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  const handleToggle = (event) => {
    const checked = event.target.checked;

    // LATEST BALANCES
    if (!checked) {
      const activeMonth = dayjs(selectedMonth);
      const asof = activeMonth.endOf('month').format('YYYY-MM-DD');

      dispatch(
        setBookAnalysisUI({
          isMonthEnd: false,
          selectedMonth: asof
        })
      );

      dispatch(
        getProviderAum({
          officeId,
          partnerId: '',
          serviceProviderId: '',
          fromDate: latestBalanceDate.format('YYYY-MM-DD')
        })
      );

      dispatch(
        getAdvisorsAcrossProviders({
          officeId,
          fromDate: latestBalanceDate.format('YYYY-MM-DD')
        })
      );

      return;
    }

    // MONTH END BALANCES
    const activeMonth = dayjs(selectedMonth);

    const asof = activeMonth.endOf('month').format('YYYY-MM-DD');

    dispatch(
      setBookAnalysisUI({
        isMonthEnd: true,
        selectedMonth: asof
      })
    );

    dispatch(
      getProviderAum({
        officeId,
        partnerId: '',
        serviceProviderId: '',
        fromDate: asof
      })
    );

    dispatch(
      getAdvisorsAcrossProviders({
        officeId,
        fromDate: asof
      })
    );
  };

  const handleMonthChange = (newValue) => {
    if (!newValue) return;

    const selected = dayjs(newValue);

    setValue('selectedMonth', selected, {
      shouldValidate: true,
      shouldDirty: true
    });

    dispatch(
      setBookAnalysisUI({
        isMonthEnd: true,
        selectedMonth: selected.endOf('month').format('YYYY-MM-DD')
      })
    );

    if (!isMonthEnd) return;

    dispatch(
      getProviderAum({
        officeId,
        partnerId: '',
        serviceProviderId: '',
        fromDate: selected.endOf('month').format('YYYY-MM-DD')
      })
    );

    dispatch(
      getAdvisorsAcrossProviders({
        officeId,
        fromDate: selected.endOf('month').format('YYYY-MM-DD')
      })
    );
  };

  const providerColumns = [
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setProviderSort, providerSort, 'name')}>
          Provider
          <SortIcon active={providerSort.field === 'name'} direction={providerSort.direction} />
        </Box>
      )
    },
    { label: 'Code' },
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setProviderSort, providerSort, 'sumSys')}>
          Amount
          <SortIcon active={providerSort.field === 'sumSys'} direction={providerSort.direction} />
        </Box>
      )
    },
    { label: 'As Of' },
    { label: '' }
  ];

  //advisor code is not relevant here
  const advisorColumns = [
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setAdvisorSort, advisorSort, 'name')}>
          Advisor
          <SortIcon active={advisorSort.field === 'name'} direction={advisorSort.direction} />
        </Box>
      )
    },
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setAdvisorSort, advisorSort, 'sumSys')}>
          Amount
          <SortIcon active={advisorSort.field === 'sumSys'} direction={advisorSort.direction} />
        </Box>
      )
    },
    { label: 'As Of' },
    { label: '' }
  ];

  return (
    <Box flex={1} display="flex" flexDirection="column" gap={2}>
      {/* SHARED CONTROLS */}
      <Box mb={2}>
        <Typography
          variant="headingText"
          sx={{
            fontSize: 24,
            fontWeight: 700
          }}>
          Provider and Advisor Summary
        </Typography>

        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: theme.palette.accent.main,
              fontSize: 11,
              whiteSpace: 'nowrap'
            }}>
            Latest Balances
          </Typography>

          <Switch
            checked={isMonthEnd}
            onChange={handleToggle}
            size="small"
            sx={{
              mx: -0.5,
              '& .MuiSwitch-switchBase': {
                p: 0.4
              },
              '& .MuiSwitch-thumb': {
                width: 14,
                height: 14
              },
              '& .MuiSwitch-track': {
                borderRadius: 10
              }
            }}
          />

          <Typography
            sx={{
              color: theme.palette.accent.main,
              fontSize: 11,
              whiteSpace: 'nowrap'
            }}>
            Month End
          </Typography>

          <Collapse in={isMonthEnd} orientation="horizontal">
            <Box
              sx={{
                width: 165,
                ml: 0.5,
                opacity: isMonthEnd ? 1 : 0,
                transition: 'opacity .25s ease'
              }}>
              <GreatDatePicker
                name="selectedMonth"
                label="Month"
                size="small"
                control={control}
                views={['year', 'month']}
                disableFuture
                minDate={dayjs('2026-01-01')}
                maxDate={lastMonth.endOf('month')}
                onAccept={handleMonthChange}
                onChange={handleMonthChange}
              />
            </Box>
          </Collapse>
        </Box>
      </Box>
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr', // Mobile
          sm: '1fr', // Small tablets
          md: '1fr 1fr' // Small laptops and up
        }}
        gap={2}>
        <PlatformAUMChart />

        <BookAUMComposition />
      </Box>

      {/* PROVIDER ACCORDION */}
      <Accordion
        expanded={expandedProvider}
        onChange={() => dispatch(setExpandedProvider())}
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #ECECEC',
          boxShadow: '0 2px 12px rgba(0,0,0,.05)',
          overflow: 'hidden',
          '&:before': {
            display: 'none'
          }
        }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
          sx={{
            px: 1.5,
            minHeight: '44px !important',
            '& .MuiAccordionSummary-content': {
              margin: '6px 0'
            }
          }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '12px'
            }}>
            Provider Summary
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <FTable
            columns={providerColumns}
            rows={sortedProviders}
            emptyMessage="No provider data found"
            containerSx={{
              backgroundColor: '#fff',
              paddingTop: 0,

              '& .MuiTable-root': {
                backgroundColor: '#fff'
              },

              '& .MuiTableHead-root': {
                backgroundColor: '#fff'
              },

              '& .MuiTableBody-root': {
                backgroundColor: '#fff'
              },

              '& .MuiTableCell-root': {
                borderBottom: 'none',
                backgroundColor: '#fff',
                py: 0.75,
                px: 1.25,
                fontSize: '12px'
              },

              '& .MuiTableRow-root': {
                backgroundColor: '#fff',
                '&:hover': {
                  backgroundColor: '#fff'
                }
              },

              '& th': {
                borderBottom: 'none',
                py: 0.9,
                px: 1.25,
                fontSize: '12px',
                fontWeight: 700
              }
            }}
            renderRow={(row) => (
              <>
                <FTableCell>{row.name || '—'}</FTableCell>

                <FTableCell>{row.code || '—'}</FTableCell>

                <FTableCell
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.accent.main,
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(10px, 1vw, 12px)'
                  }}>
                  R {row.sumSys?.toLocaleString() ?? '—'}
                </FTableCell>

                <FTableCell>{row.asof ? dayjs(row.asof).format('YYYY-MM-DD') : '—'}</FTableCell>

                <FTableCell sx={{ width: 100 }}>
                  <PrimaryButton
                    size="small"
                    sx={{
                      minWidth: 78,
                      px: 1.5,
                      py: 0.25,
                      fontSize: '12px',
                      height: 30
                    }}
                    onClick={() => navigate(`/my-practice/provider-book-analysis/${row.id}`)}>
                    View
                  </PrimaryButton>
                </FTableCell>
              </>
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* ADVISOR ACCORDION */}
      <Accordion
        expanded={expandedAdvisor}
        onChange={() => dispatch(setExpandedAdvisor())}
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #ECECEC',
          boxShadow: '0 2px 12px rgba(0,0,0,.05)',
          overflow: 'hidden',
          '&:before': {
            display: 'none'
          }
        }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.5rem' }} />}
          sx={{
            px: 1.5,
            minHeight: '44px !important',
            '& .MuiAccordionSummary-content': {
              margin: '6px 0'
            }
          }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '12px'
            }}>
            Advisor Summary
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <FTable
            columns={advisorColumns}
            rows={sortedAdvisors}
            emptyMessage="No advisor data found"
            containerSx={{
              backgroundColor: '#fff',
              paddingTop: 0,

              '& .MuiTable-root': {
                backgroundColor: '#fff'
              },

              '& .MuiTableHead-root': {
                backgroundColor: '#fff'
              },

              '& .MuiTableBody-root': {
                backgroundColor: '#fff'
              },

              '& .MuiTableRow-root': {
                backgroundColor: '#fff',
                '&:hover': {
                  backgroundColor: '#fff'
                }
              },

              '& .MuiTableCell-root': {
                backgroundColor: '#fff',
                borderBottom: 'none',
                py: 0.75,
                px: 1.25,
                fontSize: '12px'
              },

              '& th': {
                borderBottom: 'none',
                py: 0.9,
                px: 1.25,
                fontSize: '0.8rem',
                fontWeight: 700
              }
            }}
            renderRow={(advisor) => (
              <>
                <FTableCell>{advisor.name || '—'}</FTableCell>

                <FTableCell
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.accent.main,
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(10px, 1vw, 12px)'
                  }}>
                  R {advisor.sumSys?.toLocaleString() ?? '—'}
                </FTableCell>

                <FTableCell>
                  {advisor.asof ? dayjs(advisor.asof).format('YYYY-MM-DD') : '—'}
                </FTableCell>

                <FTableCell sx={{ width: 60 }}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/my-practice/advisor-book-analysis/${advisor.id}`)}>
                    <img
                      src={SilverBoomReport}
                      alt="View Book"
                      style={{
                        width: 18,
                        height: 18
                      }}
                    />
                  </IconButton>
                </FTableCell>
              </>
            )}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default ProviderAndAdvisorSummary;
